import {register} from '../vendor/theme-scripts/theme-sections';
import scrollTo from '../util/scroll-to';
import {slider} from '../features/slider';
import parallaxHero from '../features/parallax-hero';
import {popoutSection} from '../features/popout';
import {swatchGridSection, Swatch} from '../features/swatch';
import {quickAddProduct} from '../features/quick-add';
import collectionFiltersForm from '../features/collection-filters-form';
import {tooltipSection} from '../features/tooltip';
import slideToggle from '../util/slide-toggle';

const selectors = {
  dataSort: 'data-sort-enabled',
  sortLinks: '[data-sort-link]',
  sortValue: 'data-value',
  sortButton: '[data-popout-toggle]',
  sortButtonText: '[data-sort-button-text]',
  collectionSidebarHeading: '[data-collection-sidebar-heading]',
  collectionSidebar: '[data-collection-sidebar]',
  collectionSidebarSlider: '[data-collection-sidebar-slider]',
  collectionSidebarSlideOut: '[data-collection-sidebar-slide-out]',
  collectionSidebarCloseButton: '[data-collection-sidebar-close]',
  showMoreOptions: '[data-show-more]',
  groupTagsButton: '[data-aria-toggle]',
  collectionNav: '[data-collection-nav]',
  linkAdd: '[data-link-add]',
  linkRemove: '[data-link-remove]',
  linkHidden: '[data-link-hidden]',
  swatch: 'data-swatch',
};

const classes = {
  expanded: 'expanded',
  expanding: 'expanding',
  noMobileAnimation: 'no-mobile-animation',
  hidden: 'is-hidden',
  active: 'is-active',
  sortActive: 'popout-list__item--current',
  sortPopoutActive: 'popout--active',
};

let sections = {};
export default class Collection {
  constructor(section) {
    this.container = section.container;
    this.sort = this.container.querySelector(`[${selectors.dataSort}]`);
    this.sortLinks = this.container.querySelectorAll(selectors.sortLinks);
    this.collectionSidebar = this.container.querySelector(selectors.collectionSidebar);
    this.collectionSidebarCloseButton = this.container.querySelector(selectors.collectionSidebarCloseButton);
    this.groupTagsButton = this.container.querySelector(selectors.groupTagsButton);
    this.collectionNav = this.container.querySelector(selectors.collectionNav);
    this.showMoreOptions = this.container.querySelectorAll(selectors.showMoreOptions);
    this.collectionSidebarHeading = this.container.querySelectorAll(selectors.collectionSidebarHeading);
    this.swatches = this.container.querySelectorAll(`[${selectors.swatch}]`);

    this.groupTagsButtonClickEvent = (evt) => this.groupTagsButtonClick(evt);
    this.collectionSidebarCloseEvent = (evt) => this.collectionSidebarClose(evt);
    this.collectionSidebarScrollEvent = () => this.collectionSidebarScroll();
    this.onClickEvent = (e) => this.onClick(e);
    this.onSortCheckEvent = (e) => this.onSortCheck(e);
    this.sidebarResizeEvent = () => this.toggleSidebarSlider();

    this.init();
  }

  init() {
    if (this.sort) {
      this.initSort();
    }

    if (this.groupTagsButton !== null) {
      this.toggleSidebarSlider();
      document.addEventListener('theme:resize', this.sidebarResizeEvent);

      this.groupTagsButton.addEventListener('click', this.groupTagsButtonClickEvent);

      // Prevent filters closing animation on page load
      setTimeout(() => {
        this.collectionSidebar.classList.remove(classes.noMobileAnimation);
      }, 1000);
    }

    if (this.collectionSidebarCloseButton !== null) {
      this.collectionSidebarCloseButton.addEventListener('click', this.collectionSidebarCloseEvent);
    }

    // Hide filters sidebar on ESC keypress
    this.container.addEventListener(
      'keyup',
      function (evt) {
        if (evt.which !== window.theme.keyboardKeys.ESCAPE) {
          return;
        }
        this.hideSidebar();
      }.bind(this)
    );

    // Show more options from the group
    if (this.showMoreOptions) {
      this.showMoreOptions.forEach((element) => {
        element.addEventListener('click', (event) => {
          event.preventDefault();

          element.parentElement.classList.add(classes.hidden);

          element.parentElement.previousElementSibling.querySelectorAll(selectors.linkHidden).forEach((link) => {
            link.classList.remove(classes.hidden);
          });
        });
      });
    }

    if (this.collectionSidebarHeading) {
      this.collectionSidebarHeading.forEach((element) => {
        element.addEventListener('click', (event) => {
          event.preventDefault();

          element.classList.toggle(classes.active);

          slideToggle(element.nextElementSibling);

          if (element.nextElementSibling.nextElementSibling) {
            slideToggle(element.nextElementSibling.nextElementSibling);
          }
        });
      });
    }

    // Init Swatches
    if (this.swatches) {
      this.swatches.forEach((swatch) => {
        new Swatch(swatch);
      });
    }

    this.showSidebarCallback(true);
  }

  collectionSidebarScroll() {
    document.dispatchEvent(
      new CustomEvent('tooltip:close', {
        bubbles: false,
        detail: {
          hideTransition: false,
        },
      })
    );
  }

  sortActions(link, submitForm = true) {
    const sort = link ? link.getAttribute(selectors.sortValue) : '';
    this.sort.setAttribute(selectors.dataSort, sort);

    const sortButtonText = this.sort.querySelector(selectors.sortButtonText);
    const sortActive = this.sort.querySelector(`.${classes.sortActive}`);
    if (sortButtonText) {
      const linkText = link ? link.textContent.trim() : '';
      sortButtonText.textContent = linkText;
    }
    if (sortActive) {
      sortActive.classList.remove(classes.sortActive);
    }
    this.sort.classList.toggle(classes.sortPopoutActive, link);

    if (link) {
      link.parentElement.classList.add(classes.sortActive);

      if (submitForm) {
        link.dispatchEvent(
          new CustomEvent('filter:submit:form', {
            bubbles: true,
            detail: {
              href: sort,
            },
          })
        );
      }
    }
  }

  onClick(e) {
    e.preventDefault();
    const sortButton = this.sort.querySelector(selectors.sortButton);
    if (sortButton) {
      sortButton.dispatchEvent(new Event('click'));
    }
    this.sortActions(e.currentTarget);
  }

  onSortCheck(e) {
    let link = null;
    if (window.location.search.includes('sort_by')) {
      const url = new window.URL(window.location.href);
      const urlParams = url.searchParams;

      for (const [key, val] of urlParams.entries()) {
        const linkSort = this.sort.querySelector(`[${selectors.sortValue}="${val}"]`);
        if (key.includes('sort_by') && linkSort) {
          link = linkSort;
          break;
        }
      }
    }

    this.sortActions(link, false);
  }

  initSort() {
    this.sortLinks.forEach((link) => {
      link.addEventListener('click', this.onClickEvent);
    });
    this.sort.addEventListener('filter:sort:check', this.onSortCheckEvent);
  }

  showSidebarCallback(onLoad = false) {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const collectionSidebarSlider = this.container.querySelector(selectors.collectionSidebarSlider);
    const collectionSidebarSlideOut = this.container.querySelector(selectors.collectionSidebarSlideOut);
    const collectionSidebarScrollable = collectionSidebarSlider || collectionSidebarSlideOut;

    if (!onLoad) {
      if (collectionSidebarSlideOut === null) {
        if (windowWidth < theme.sizes.small) {
          const scrollTopPosition = this.collectionNav ? this.collectionNav.getBoundingClientRect().top : this.groupTagsButton.getBoundingClientRect().top;
          scrollTo(scrollTopPosition);
        } else {
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        }
      }

      if (windowWidth < theme.sizes.small || collectionSidebarSlideOut !== null) {
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: collectionSidebarScrollable}));
      }
    }

    if (collectionSidebarScrollable) {
      collectionSidebarScrollable.addEventListener('scroll', this.collectionSidebarScrollEvent);
    }
  }

  hideSidebar() {
    const collectionSidebarSlider = this.container.querySelector(selectors.collectionSidebarSlider);
    const collectionSidebarSlideOut = this.container.querySelector(selectors.collectionSidebarSlideOut);
    const collectionSidebarScrollable = collectionSidebarSlider || collectionSidebarSlideOut;

    this.groupTagsButton.setAttribute('aria-expanded', 'false');
    this.collectionSidebar.classList.add(classes.expanding);
    this.collectionSidebar.classList.remove(classes.expanded);

    if (collectionSidebarScrollable) {
      collectionSidebarScrollable.removeEventListener('scroll', this.collectionSidebarScrollEvent);
    }

    setTimeout(() => {
      this.collectionSidebar.classList.remove(classes.expanding);
    }, 500);
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
  }

  toggleSidebarSlider() {
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

    if (windowWidth < theme.sizes.small) {
      this.hideSidebar();
    } else if (this.collectionSidebar.classList.contains(classes.expanded)) {
      this.showSidebarCallback();
    }
  }

  collectionSidebarClose(evt) {
    evt.preventDefault();
    this.hideSidebar();
  }

  groupTagsButtonClick() {
    if (this.collectionSidebar.classList.contains(classes.expanded)) {
      this.showSidebarCallback();
    } else {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
    }
  }

  onUnload() {
    document.removeEventListener('theme:resize', this.sidebarResizeEvent);
  }
}

const collectionSection = {
  onLoad() {
    sections[this.id] = new Collection(this);
  },
  onUnload() {
    sections[this.id].onUnload();
  },
};

register('collection', [slider, parallaxHero, quickAddProduct, collectionSection, popoutSection, swatchGridSection, collectionFiltersForm, tooltipSection]);
